import { type ElementType, type AllHTMLAttributes, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import { textRecipe } from './text.css';

export type TextVariant = 'pageHeader' | 'cardTitle' | 'body' | 'buttonText' | 'caption' | 'smallLabel' | 'sectionHeader';
export type TextColor = 'primary' | 'secondary' | 'muted' | 'error' | 'success' | 'warning' | 'brand' | 'inherit';

export interface TextProps extends Omit<AllHTMLAttributes<HTMLElement>, 'as' | 'color'> {
  variant?: TextVariant;
  color?: TextColor;
  truncate?: boolean;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

// Heading variants get a heading element by default so screen-reader heading
// navigation works on every page. Callers can still override via `as` when a
// non-heading semantic is intended (e.g., a list-card title rendered inside
// an already-headinged section).
const DEFAULT_TAG_FOR_VARIANT: Partial<Record<TextVariant, ElementType>> = {
  pageHeader: 'h1',
  sectionHeader: 'h2',
  cardTitle: 'h3',
};

export function Text({
  variant,
  color,
  truncate,
  as,
  className,
  children,
  ...props
}: TextProps) {
  const Tag: ElementType = as ?? (variant ? DEFAULT_TAG_FOR_VARIANT[variant] ?? 'span' : 'span');
  return (
    <Tag
      className={clsx(textRecipe({ variant, color, truncate: truncate ? true : undefined }), className)}
      {...props}
    >
      {children}
    </Tag>
  );
}
